import React from 'react';
import { 
  AlertTriangle, 
  Phone, 
  Mail, 
  MessageCircle, 
  Building2, 
  Clock, 
  ChevronRight,
  ShieldX
} from 'lucide-react';
import { 
  TRANSPORTADORA_CONFIG, 
  getWhatsAppSuporteUrl, 
  getEmailSuporteUrl 
} from '../../config/transportadoraConfig';

interface UsuarioInativoAlertProps {
  userEmail?: string;
  userName?: string;
  onDismiss?: () => void;
  variant?: 'card' | 'banner';
}

export const UsuarioInativoAlert: React.FC<UsuarioInativoAlertProps> = ({
  userEmail,
  userName,
  onDismiss,
  variant = 'card'
}) => {
  const whatsappUrl = getWhatsAppSuporteUrl(userEmail, userName);
  const emailUrl = getEmailSuporteUrl(userEmail, userName);

  if (variant === 'banner') {
    return (
      <div 
        id="usuario-inativo-banner"
        className="w-full bg-gradient-to-r from-amber-500/15 via-rose-500/15 to-red-500/15 border border-amber-400/40 rounded-xl p-4 text-slate-800 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-500/20 text-amber-700 rounded-lg flex-shrink-0 mt-0.5">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <span>Conta de Usuário Inativa</span>
              <span className="text-[10px] uppercase font-extrabold px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full border border-amber-200">
                Acesso Bloqueado
              </span>
            </h4>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              O seu cadastro encontra-se <strong>inativo</strong> no sistema. Por favor, <strong>entre em contato com a transportadora</strong> para regularizar e reativar o seu acesso.
            </p>
            
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-sm transition"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                Falar com a Transportadora (WhatsApp)
              </a>
              <a
                href={emailUrl}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold shadow-sm transition"
              >
                <Mail className="w-3.5 h-3.5" />
                Enviar E-mail
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      id="usuario-inativo-card"
      className="bg-white border-2 border-rose-200 rounded-2xl p-5 shadow-xl text-slate-800 overflow-hidden relative"
    >
      {/* Barra superior de destaque */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-500 via-rose-500 to-red-600"></div>

      <div className="flex items-start gap-4 pt-1">
        <div className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 flex-shrink-0 shadow-inner">
          <ShieldX className="w-6 h-6" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-black uppercase tracking-wider text-rose-700 bg-rose-100 px-2.5 py-0.5 rounded-full border border-rose-200">
              Acesso Temporariamente Suspenso
            </span>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-xs text-slate-400 hover:text-slate-600 font-bold"
              >
                Fechar
              </button>
            )}
          </div>

          <h3 className="text-base font-bold text-slate-900 mt-2">
            Seu usuário está <span className="text-rose-600">inativo</span> no sistema
          </h3>

          <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
            Identificamos que seu cadastro de acesso foi desativado. Para liberar as funcionalidades e restaurar seu login, <strong>entre em contato com a transportadora</strong> pelos canais de atendimento abaixo:
          </p>

          {/* Dados de Contato com a Transportadora */}
          <div className="mt-3.5 bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-800">
              <Building2 className="w-4 h-4 text-slate-500" />
              <span>{TRANSPORTADORA_CONFIG.nome}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t border-slate-200 text-xs">
              <div className="flex items-center gap-2 text-slate-600">
                <Phone className="w-3.5 h-3.5 text-slate-400" />
                <span>Tel: <strong>{TRANSPORTADORA_CONFIG.telefoneFormatado}</strong></span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                <span>{TRANSPORTADORA_CONFIG.horarioAtendimento}</span>
              </div>
            </div>
          </div>

          {/* Botões de Ação Imediata */}
          <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-600/20 transition active:scale-[0.98]"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Contatar pelo WhatsApp</span>
              <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-70" />
            </a>

            <a
              href={emailUrl}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold shadow-sm transition active:scale-[0.98]"
            >
              <Mail className="w-4 h-4" />
              <span>Enviar E-mail</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
